/* eslint-disable prefer-promise-reject-errors */
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { IUser } from 'projects/common/src/app/interfaces';
import { IMachineContext } from 'projects/common/src/app/interfaces/machine-context';
import { IMachineType } from 'projects/common/src/app/interfaces/machine-type';
import { IRawComponent } from 'projects/common/src/app/interfaces/remote-models';
import { CacheService, ParameterHandlerService } from 'projects/common/src/app/services';
import { ApiService, UrlBuilderService } from 'projects/communication/src/app/services';
import { Subject } from 'rxjs';
import { IShift } from 'src/app/modules/core/interfaces';
import { IWidgetGetter } from 'src/app/modules/widget/interfaces/widget-data/widget-getter';
import { ServerConfigService } from 'src/app/services/server-config.service';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root',
})
export class WidgetDataService extends ApiService {
  public paramChanges: Subject<any> = new Subject<any>();
  private parameterHandler: ParameterHandlerService;

  constructor(
    public http: HttpClient,
    public configServer: ServerConfigService,
    private cacheService: CacheService,
    private parameterHandlerService: ParameterHandlerService,
    private urlBuilder: UrlBuilderService,
  ) {
    super(http, configServer);
  }

  // Método para establecer la dependencia del ParameterHandlerService
  public setParameterHandler(parameterHandler: ParameterHandlerService): void {
    this.parameterHandler = parameterHandler;
  }

  /**
   * Function to get the data for widget
   *
   * @param widgetKey Widget key for dashboard
   * @param params Parameters to get widget data
   */
  public getWidgetData(widgetKey: string, widgetGetter: IWidgetGetter): Promise<any> {
    return new Promise<any>((resolve, reject) => {
      const params = this.buildParams(widgetGetter);
      this.getComponentsByUser().then(
        (components: IRawComponent[]) => {
          // Get the endpoint of component and put the params to get the widget data
          const found = components.find((value) => value.name === widgetKey);
          if (found) {
            this.get(found.endpoint, params)
              .toPromise()
              .then(
                (value) => resolve(value),
                () => {},
              );
          } else {
            reject(null);
          }
        },
        () => {},
      );
    });
  }

  /**
   * Function to build param request
   *
   * @param params Parameters to build param request
   */
  private buildParams(widgetGetter: IWidgetGetter): any {
    const params: { [key: string]: any } = {};
    const parameter = this.parameterHandlerService.parameters;

    if (widgetGetter.hasCompany) {
      params.companyId = String((parameter.item('user-data').value as IUser).company.id);
    }

    if (widgetGetter.hasDate) {
      params.inputDate = String(parameter.item('date').value);
    }

    if (widgetGetter.hasMachineType) {
      const currentMachine = (parameter.item('machine-context').value as IMachineContext).name;
      const machineCollection = parameter.item('machine-collection').value as IMachineType[];
      const machine = machineCollection.find((value) => String(value.name).toLowerCase() === String(currentMachine).toLowerCase());

      params.machineTypeId = machine.machineTypeId;
    }

    if (widgetGetter.hasShift) {
      params.shiftId = String((parameter.item('shift').value as IShift).shiftId);
    }

    if (widgetGetter.hasZone) {
      params.zoneId = this.cacheService.session('selectedZone').zoneId;
    }

    if (widgetGetter.hasOrderBy) {
      const orderBy = this.cacheService.local('orderBy');
      params.orderBy = orderBy || 1;
    }

    if (widgetGetter.hasResultType) {
      params.resultType = 1;
    }

    return params;
  }

  /**
   * Function to get the components by user
   */
  private getComponentsByUser(): Promise<any> {
    const endpoint = `${this.urlBuilder.securityUrl}/component/getusercomponents`;
    const params = {
      userId: String((this.parameterHandlerService.parameters.item('user-data').value as IUser).id),
    };
    return this.get(endpoint, params).toPromise();
  }
}
